/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import UndoIcon from '@atlaskit/icon/core/undo';
import { Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { SourcesStack } from '../sources-stack/sources-stack';

const messages = defineMessages({
	referencing: {
		id: 'search_ai.sources_bar_referencing',
		defaultMessage: 'Referencing',
		description: 'Prefix label on the sources bar',
	},
	sources_count: {
		id: 'search_ai.sources_bar_sources_count',
		defaultMessage: '{numSources} {numSources, plural, one {source} other {sources}}',
		description: 'Source count label on the sources bar e.g. "4 sources"',
	},
});

const styles = cssMap({
	button: {
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		backgroundColor: token('elevation.surface.sunken'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		backgroundImage: `linear-gradient(90deg, ${token('elevation.surface')} 0%, transparent 100%), linear-gradient(${token('color.border.disabled')} 1px, transparent 1px), linear-gradient(90deg, ${token('color.border.disabled')} 1px, transparent 1px)`,
		backgroundSize: `100% 100%, 24px 24px, 24px 24px`,
		backgroundPosition: `0 0, 6px 4px, 6px 4px`,
		borderRadius: token('radius.medium'),
	},
	flippedIcon: {
		transform: 'rotate(180deg)',
		display: 'inline-flex',
	},
});

type SourcesBarProps = {
	sources: NLPSearchResultSource[];
	numSources: number;
	onClick: () => void;
	triggerRef?: React.RefObject<HTMLButtonElement>;
};

/**
 * Full-width pressable sources bar for the SourcesInSidePanel experience.
 */
export const SourcesBar = ({
	sources,
	numSources,
	onClick,
	triggerRef,
}: SourcesBarProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Pressable xcss={styles.button} onClick={onClick} testId="sources-bar-button" ref={triggerRef}>
			<Inline space="space.100" alignBlock="center">
				<Inline space="space.050" alignBlock="center">
					<span css={styles.flippedIcon}>
						<UndoIcon label="" color={token('color.icon.subtlest')} />
					</span>
					<Inline space="space.050" alignBlock="center">
						<Text color="color.text.subtlest" size="small">
							{formatMessage(messages.referencing)}
						</Text>
						<Text color="color.text.subtle" size="small" weight="medium">
							{formatMessage(messages.sources_count, { numSources })}
						</Text>
					</Inline>
				</Inline>
				<SourcesStack sources={sources} maxSourcesInStack={4} shuffled />
			</Inline>
			<ArrowRightIcon label="" color={token('color.icon.subtle')} />
		</Pressable>
	);
};
